import type { VariantProps } from 'class-variance-authority'

import { Button as ButtonPrimitive } from '@base-ui/react/button'
import { cva } from 'class-variance-authority'
import { cn } from '@/lib/utils'

const buttonVariants = cva(
    'group/button inline-flex shrink-0 items-center justify-center rounded-lg border border-transparent bg-clip-padding text-sm font-semibold whitespace-nowrap transition-all outline-none select-none focus-visible:ring-3 focus-visible:ring-ring/30 active:not-aria-[haspopup]:translate-y-px disabled:pointer-events-none disabled:opacity-50 data-disabled:pointer-events-none data-disabled:opacity-50 aria-invalid:border-destructive [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*=size-])]:size-4',
    {
        variants: {
            variant: {
                default: 'bg-primary text-primary-foreground shadow-[0_1px_2px_rgb(23_43_77/0.24)] hover:bg-primary/90',
                outline: 'border-border bg-card text-foreground shadow-card hover:bg-muted aria-expanded:bg-muted',
                secondary: 'bg-secondary text-secondary-foreground hover:bg-navy-soft aria-expanded:bg-navy-soft',
                ghost: 'text-foreground hover:bg-muted aria-expanded:bg-muted',
                destructive: 'bg-destructive text-white shadow-[0_1px_2px_rgb(181_72_58/0.3)] hover:bg-destructive/90 focus-visible:ring-destructive/30',
                'destructive-soft': 'bg-expense-soft text-expense-strong hover:bg-[color-mix(in_oklab,var(--expense-soft),var(--expense)_12%)] focus-visible:ring-destructive/30',
                link: 'text-primary underline-offset-4 hover:underline'
            },
            size: {
                default: 'h-10 gap-2 px-4',
                sm: 'h-9 gap-1.5 px-3 text-[0.8125rem]',
                xs: 'h-8 gap-1.5 rounded-md px-2.5 text-xs',
                lg: 'h-11 gap-2 px-5 text-[0.9375rem]',
                icon: 'size-10',
                'icon-sm': 'size-9',
                'icon-xs': 'size-8 rounded-md'
            }
        },
        defaultVariants: {
            variant: 'default',
            size: 'default'
        }
    }
)

type ButtonProps = ButtonPrimitive.Props & VariantProps<typeof buttonVariants>

function Button({
    className,
    variant = 'default',
    size = 'default',
    ...props
}: ButtonProps) {
    return (
        <ButtonPrimitive
            data-slot='button'
            className={cn(buttonVariants({ variant, size, className }))}
            {...props}
        />
    )
}

export { Button, buttonVariants }
export type { ButtonProps }
